import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Mail, MapPin, Phone, Clock } from 'lucide-react';

export function ContactPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 lg:px-16">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xs tracking-[0.3em] uppercase text-neutral-500 mb-6 block"
            >
              {t('contact.getInTouch')}
            </motion.span>
            <h1 className="text-5xl md:text-7xl tracking-[0.15em] uppercase font-light mb-8">
              {t('contact.title')}
            </h1>
            <p className="text-xl text-neutral-600 tracking-wide max-w-2xl mx-auto">
              {t('contact.reachOut')}
            </p>
          </motion.div>

          {/* Company Info & Map */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              {/* Email */}
              <motion.a
                href="mailto:info@matrixstone.com.tr"
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.45 }}
                whileHover={{ x: 8, scale: 1.02 }}
                className="bg-white p-8 shadow-lg shadow-black/5 hover:shadow-xl hover:shadow-black/10 transition-all duration-500 flex items-start gap-6 group"
              >
                <motion.div
                  whileHover={{ rotate: 5 }}
                  className="w-16 h-16 bg-neutral-900 flex items-center justify-center flex-shrink-0 group-hover:bg-neutral-800 transition-colors duration-300"
                >
                  <Mail className="w-8 h-8 text-white" />
                </motion.div>
                <div>
                  <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">
                    E-MAIL
                  </h3>
                  <p className="text-lg text-neutral-900 group-hover:text-neutral-600 transition-colors">
                    info@matrixstone.com.tr
                  </p>
                </div>
              </motion.a>

              {/* Phone */}
              <motion.a
                href={`tel:${t('contact.phoneValue').replace(/\s/g, '')}`}
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                whileHover={{ x: 8, scale: 1.02 }}
                className="bg-white p-8 shadow-lg shadow-black/5 hover:shadow-xl hover:shadow-black/10 transition-all duration-500 flex items-start gap-6 group"
              >
                <motion.div
                  whileHover={{ rotate: 5 }}
                  className="w-16 h-16 bg-neutral-900 flex items-center justify-center flex-shrink-0 group-hover:bg-neutral-800 transition-colors duration-300"
                >
                  <Phone className="w-8 h-8 text-white" />
                </motion.div>
                <div>
                  <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">
                    {t('contact.phone')}
                  </h3>
                  <p className="text-lg text-neutral-900 group-hover:text-neutral-600 transition-colors">
                    {t('contact.phoneValue')}
                  </p>
                </div>
              </motion.a>

              {/* Address */}
              <motion.a
                href={`https://maps.google.com/?q=${encodeURIComponent(t('contact.addressValue'))}`}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                whileHover={{ x: 8, scale: 1.02 }}
                className="bg-white p-8 shadow-lg shadow-black/5 hover:shadow-xl hover:shadow-black/10 transition-all duration-500 flex items-start gap-6 group"
              >
                <motion.div
                  whileHover={{ rotate: 5 }}
                  className="w-16 h-16 bg-neutral-900 flex items-center justify-center flex-shrink-0 group-hover:bg-neutral-800 transition-colors duration-300"
                >
                  <MapPin className="w-8 h-8 text-white" />
                </motion.div>
                <div>
                  <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">
                    {t('contact.address')}
                  </h3>
                  <p className="text-lg text-neutral-900 group-hover:text-neutral-600 transition-colors leading-relaxed">
                    {t('contact.addressValue')}
                  </p>
                </div>
              </motion.a>

              {/* Business Hours */}
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="bg-white p-8 shadow-lg shadow-black/5"
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-neutral-900 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wide mb-4">
                      {t('contact.businessHours')}
                    </h3>
                    <div className="space-y-2 text-neutral-700">
                      <p className="flex justify-between gap-8">
                        <span>{t('contact.mondayFriday')}</span>
                        <span>08:00 - 18:00</span>
                      </p>
                      <p className="flex justify-between gap-8">
                        <span>{t('contact.saturday')}</span>
                        <span>09:00 - 16:00</span>
                      </p>
                      <p className="flex justify-between gap-8">
                        <span>{t('contact.sunday')}</span>
                        <span>{t('contact.closed')}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Google Maps */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative h-[700px] bg-neutral-100 overflow-hidden shadow-2xl shadow-black/10"
            >
              <iframe
                src={`https://maps.google.com/maps?q=${encodeURIComponent('İOSB Beyaz Tower Plaza Başakşehir İstanbul')}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Matrix Stone Location"
                className="absolute inset-0"
              />

              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm p-6 shadow-xl">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-neutral-900 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">
                      {t('contact.address')}
                    </h3>
                    <p className="text-lg text-neutral-900 mb-4 leading-relaxed">
                      {t('contact.addressValue')}
                    </p>
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(t('contact.addressValue'))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block px-6 py-2 bg-neutral-900 text-white hover:bg-neutral-800 tracking-[0.15em] uppercase text-xs transition-colors duration-300"
                    >
                      {t('contact.openInMaps')}
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-16 bg-gradient-to-br from-neutral-900 to-neutral-800">
        <div className="max-w-[1200px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl tracking-[0.15em] uppercase font-light text-white mb-6">
              {t('contact.readyToGetStarted')}
            </h2>
            <p className="text-xl text-neutral-300 tracking-wide mb-10 max-w-2xl mx-auto">
              {t('contact.supportProject')}
            </p>
            <motion.a
              href="mailto:info@matrixstone.com.tr"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block px-12 py-5 bg-white text-neutral-900 hover:bg-neutral-100 tracking-[0.2em] uppercase text-sm transition-colors duration-300 shadow-lg"
            >
              {t('contact.sendEmail')}
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
